import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Zap,
  TrendingUp,
  Calendar,
  Clock,
  RefreshCw,
  Activity,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUsageData } from '../contexts/UsageDataContext';
import { formatCurrency, formatDateTime, getStatusColor, getUserUsageLogs } from '../api/usage';
import { FREE_REQUESTS_PER_MONTH } from '../config/constants';
import PageHeader from './PageHeader';
import './AnalyticsPage.css';

const AnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [avgCallsPerDay, setAvgCallsPerDay] = useState(0);
  const [logsToShow, setLogsToShow] = useState(10);
  const [hasMoreLogs, setHasMoreLogs] = useState(true);
  const { user } = useAuth();
  const { usageData, currentMonthUsage, recentLogs, loading, logsLoading, dataLoaded, loadUsageData, loadRecentLogs, refreshData } = useUsageData();

  // Load data only once when user is available and data hasn't been loaded yet
  useEffect(() => {
    if (user && !dataLoaded) {
      // Load all data when no date range is selected (initial load)
      loadUsageData();
      loadRecentLogs(undefined, undefined, 10).then((logs) => {
        // Check if we got a full batch (10 logs)
        if (logs.length < 10) {
          setHasMoreLogs(false);
        }
      });
    }
  }, [user, dataLoaded, loadUsageData, loadRecentLogs]);

  // Load data with date range when date range changes (only for filtering)
  useEffect(() => {
    if (user && dataLoaded && dateRange.start && dateRange.end) {
      // Load data for specific date range when user explicitly sets dates
      loadUsageData(dateRange.start, dateRange.end);
      setLogsToShow(10);
      setHasMoreLogs(true);
      loadRecentLogs(dateRange.start, dateRange.end, 10).then((logs) => {
        // Check if we got a full batch (10 logs)
        if (logs.length < 10) {
          setHasMoreLogs(false);
        }
      });
    }
  }, [dateRange.start, dateRange.end, user, dataLoaded, loadUsageData, loadRecentLogs]);

  // Calculate average calls per day from usage data
  useEffect(() => {
    const calculateAvgCallsPerDay = () => {
      if (dateRange.start && dateRange.end) {
        // For custom date range, use usageData
        if (!usageData?.totalRequests) {
          setAvgCallsPerDay(0);
          return;
        }
        
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysInPeriod = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
        const avgCalls = usageData.totalRequests / daysInPeriod;
        setAvgCallsPerDay(avgCalls);
      } else {
        // For current month, use currentMonthUsage
        setAvgCallsPerDay(currentMonthUsage?.avgCallsPerDay || 0);
      }
    };

    calculateAvgCallsPerDay();
  }, [usageData, currentMonthUsage, dateRange.start, dateRange.end]);

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetDateRange = () => {
    setDateRange({ start: '', end: '' });
    setLogsToShow(10);
    setHasMoreLogs(true);
    // Refresh data to show current month data
    loadUsageData();
    loadRecentLogs(undefined, undefined, 10);
  };

  const handleShowMore = async () => {
    const newCount = logsToShow + 10;
    setLogsToShow(newCount);
    // Load 10 more logs starting from the current count
    const logsLoaded = await loadRecentLogs(dateRange.start, dateRange.end, 10, logsToShow, true);
    // Check if we got a full batch (10 logs) - if not, there are no more logs
    if (logsLoaded.length < 10) {
      setHasMoreLogs(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="analytics-page">
      <PageHeader
        icon={Activity}
        title="Usage"
        subtitle="Detailed usage statistics and API call logs"
        actions={
          <button 
            onClick={() => refreshData(dateRange.start, dateRange.end)}
            disabled={loading || logsLoading}
            className="refresh-button"
          >
            <RefreshCw className={`refresh-icon ${loading || logsLoading ? 'spinning' : ''}`} size={16} />
            {loading || logsLoading ? 'Loading...' : 'Refresh'}
          </button>
        }
      />

      {/* Date Range Filter */}
      <div className="date-filter-card">
        <div className="date-inputs">
          <div className="date-input-group">
            <label>From</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              placeholder="Start date"
            />
          </div>
          <div className="date-input-group">
            <label>To</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              placeholder="End date"
            />
          </div>
          <button onClick={resetDateRange} className="reset-button">
            Clear
          </button>
        </div>
      </div>

      {/* Usage Summary Cards */}
      <div className="analytics-stats">
        <div className="stats-grid-usage">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-info">
                <div className="stat-label">Total API Calls</div>
                <div className="stat-value">
                  {loading ? '...' : (dateRange.start && dateRange.end ? usageData?.totalRequests?.toLocaleString() : currentMonthUsage?.requests?.toLocaleString()) || '0'}
                </div>
                <div className="stat-description">
                  {dateRange.start && dateRange.end ? 'Selected period' : 'This month'}
                </div>
              </div>
              <div className="stat-icon">
                <Zap className="icon" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-info">
                <div className="stat-label">Avg Calls/Day</div>
                <div className="stat-value">
                  {loading ? '...' : avgCallsPerDay.toFixed(2)}
                </div>
                <div className="stat-description">
                  {dateRange.start && dateRange.end ? 'Selected period' : 'This month'}
                </div>
              </div>
              <div className="stat-icon">
                <Calendar className="icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent API Calls */}
      <div className="recent-calls-card">
        <div className="card-header">
          <div className="header-content">
            <div className="icon-wrapper">
              <BarChart3 className="icon" />
            </div>
            <div>
              <h3 className="card-title">Recent API Calls</h3>
              <p className="card-subtitle">
                {dateRange.start && dateRange.end 
                  ? `API requests from ${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}`
                  : 'Recent API requests'
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="calls-table">
          <div className="table-header">
            <div>Endpoint</div>
            <div>Method</div>
            <div>Status</div>
            <div>Response Time</div>
            <div>Cost</div>
            <div>Date</div>
          </div>
          
          {logsLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading API calls...</p>
            </div>
          ) : recentLogs.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} />
              <p>No API calls found for the selected period</p>
            </div>
          ) : (
            <>
              {recentLogs.slice(0, logsToShow).map((log) => (
                <div key={log.id} className="table-row">
                  <div className="endpoint">{log.endpoint}</div>
                  <div className="method">{log.method}</div>
                  <div 
                    className="status"
                    style={{ color: getStatusColor(log.status_code) }}
                  >
                    {log.status_code}
                  </div>
                  <div className="response-time">{log.response_time_ms}ms</div>
                  <div className="cost">{formatCurrency(log.cost_usd)}</div>
                  <div className="date">{formatDateTime(log.created_at)}</div>
                </div>
              ))}
              {hasMoreLogs && recentLogs.length >= logsToShow && recentLogs.length > 0 && (
                <button
                  onClick={handleShowMore}
                  className="show-more-button"
                  disabled={logsLoading}
                >
                  {logsLoading ? 'Loading...' : 'Show More'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
